import { modelsApi } from '@/api/models.api';
import { ModelPreview } from '@/components/project-detail/ModelPreview';
import { ModelUploadZone } from '@/components/project-detail/ModelUploadZone';
import type { Model3DModel, ModelType } from '@/types/project.types';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

interface ModelColumnProps {
  title: string;
  modelType: ModelType;
  projectId: string;
  model?: Model3DModel | null;
  onUpload: () => Promise<void>; // Called after upload completes for refetch
}

export const ModelColumn: FC<ModelColumnProps> = ({ title, modelType, projectId, model, onUpload }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  // Local state to immediately hide model preview when deleted
  const [localModel, setLocalModel] = useState<Model3DModel | null>(model ?? null);
  // Track if we just deleted the model to prevent showing it again before refetch completes
  const justDeletedRef = useRef(false);

  // Update local model when prop changes
  useEffect(() => {
    if (model) {
      // Prop has a model - update localModel (e.g., new model uploaded)
      // Reset justDeleted flag when new model arrives
      justDeletedRef.current = false;
      setLocalModel(model);
    } else if (!model && !justDeletedRef.current) {
      // Prop is null and we didn't just delete - prop was updated after deletion from parent
      setLocalModel(null);
    }
    // If justDeletedRef is true and prop is null, keep localModel null (already set in handleDelete)
  }, [model]);

  const handleUpload = async (file: File) => {
    setIsUploading(true);
    setUploadProgress(0);
    setCurrentStep('');
    try {
      // Upload with progress tracking
      await modelsApi.uploadWithProgress(
        projectId,
        file,
        modelType,
        (progress, step) => {
          setUploadProgress(progress);
          setCurrentStep(step);
        },
        (error) => {
          console.error('Upload error:', error);
        }
      );

      toast.success(`${modelType} model uploaded`);

      // Call parent callback for refetch
      await onUpload();
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setCurrentStep('');
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow flex flex-col gap-4">
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>

      {localModel ? (
        <div className="flex flex-col gap-4">
          {/* Preview */}
          <div className="aspect-video bg-gray-100 rounded overflow-hidden">
            <ModelPreview modelId={localModel.id} alt={`${title} preview`} />
          </div>
        </div>
      ) : (
        /* Upload zone */
        <ModelUploadZone
          onUpload={handleUpload}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          currentStep={currentStep}
        />
      )}
    </div>
  );
};
